import React, { FunctionComponent, useEffect, useMemo } from 'react';
import '../productPage/producthome.css';
import { Box, SxProps } from '@mui/material';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AboutInvestorRelationsContent } from '../../graphql/pimcore/modules/siteContent/types/AboutInvestorRelationsContent';
import BreadCrumbs from '../common/BreadCrumbs';
import OverviewPage from './OverviewPage';
import { useResponsive } from '../../contexts/ResponsiveContext';
import TechnicalResources from '../automotivePage/TechnicalResources';
import { PrefilteredResourceLibraryItemsProvider } from '../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import TabsComponent, { GetResponsiveWidth } from '../common/TabsComponent';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`product-tabpanel-${index}`}
            aria-labelledby={`product-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

export const getTabTypography = (isScreenMd: boolean, isScreenSm: boolean): SxProps => ({
    fontSize: isScreenMd || isScreenSm ? 14 : 18,
    fontFamily: 'Poppins',
});

interface InvestorRelationHomeProps {
    cmsContent: AboutInvestorRelationsContent;
}

const InvestorRelationHome: FunctionComponent<InvestorRelationHomeProps> = ({ cmsContent }) => {
    const bannerStyle = {
        display: 'flex',
        flexDirection: 'column' as const,
        backgroundImage: `linear-gradient(rgba(183, 83, 50, 0.8), rgba(183, 83, 50, 0.8)), url(${cmsContent.pageTitleBackgroundImage.imageUrl})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        width: '100%',
        color: 'white',
    };
    const [url, setUrl] = React.useState(0);
    const [openMenu, setOpenMenu] = React.useState(false); // 用於控制小螢幕時的展開/收合

    const { isScreenMd, isScreenSm, isScreenXs } = useResponsive();

    const tabTypography = getTabTypography(isScreenMd, isScreenSm);

    const location = useLocation();
    const navigate = useNavigate();

    const pathMap = useMemo(
        () => [
            '/About/InvestorRelations/Overview',
            '/About/InvestorRelations/FinancialReports',
            '/About/InvestorRelations/InvestorConference',
            '/About/InvestorRelations/ShareholderMeetings',
            '/About/InvestorRelations/Newsroom',
        ],
        []
    );

    useEffect(() => {
        const newUrl = pathMap.indexOf(location.pathname);
        setUrl(newUrl >= 0 ? newUrl : 0);
    }, [location.pathname, pathMap]);

    const handleChange = (event: React.SyntheticEvent | null, newUrl: number) => {
        setUrl(newUrl);
        navigate(pathMap[newUrl] || pathMap[0]!);
    };

    const menuItems = [
        { label: cmsContent.Overview.title.content, value: 0 },
        { label: cmsContent.FinancialReports.title.content, value: 1 },
        { label: cmsContent.InvestorConference.title.content, value: 2 },
        { label: cmsContent.ShareholderMeetings.title.content, value: 3 },
        { label: cmsContent.Resources.title.content, value: 4 },
    ];

    return (
        <div key={location.pathname} style={styles.productPageContainer}>
            <div style={styles.breadCrumbs}>
                <Box
                    sx={{
                        width: GetResponsiveWidth(),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        margin: 'auto',
                    }}
                >
                    <BreadCrumbs navigationPath="/Home/About/InvestorRelations" />
                </Box>
            </div>

            <div style={bannerStyle}>
                <Box
                    sx={{
                        width: GetResponsiveWidth(),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        margin: 'auto',
                        height: '180px',
                    }}
                >
                    <span
                        style={{
                            ...styles.bannerFont,
                            fontSize: isScreenXs ? '22px' : styles.bannerFont.fontSize,
                            marginBottom: isScreenXs ? '6px' : styles.bannerFont.marginBottom,
                        }}
                    >
                        {cmsContent.pageTitle.content}
                    </span>
                    <span
                        style={{
                            ...styles.bannerFont2,
                            fontSize: isScreenXs ? '14px' : styles.bannerFont2.fontSize,
                            marginBottom: '2px',
                        }}
                    >
                        {cmsContent.pageSubtitle.content.split('\n').map((line, index, arr) => (
                            <React.Fragment key={index}>
                                {line}
                                {index < arr.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </span>
                </Box>
            </div>

            <Box
                sx={{
                    zIndex: 1,
                    width: '100%',
                    backgroundColor: isScreenSm || isScreenXs ? 'white' : '#7A7C81',
                }}
                data-testid={'outter_box'}
            >
                <TabsComponent
                    isMenuExpandMoreIcon
                    url={url}
                    setOpenMenu={setOpenMenu}
                    openMenu={openMenu}
                    handleChange={handleChange}
                    menuItems={menuItems}
                    tabTypography={tabTypography}
                />

                <CustomTabPanel value={url} index={0}>
                    <OverviewPage cmsContent={cmsContent.Overview} />
                </CustomTabPanel>
                <CustomTabPanel value={url} index={1}>
                    {location.pathname.includes('FinancialReports') && (
                        <PrefilteredResourceLibraryItemsProvider
                            initialFilters={{
                                // On change of resourceTypeKeys, please also update the related Pimcore role
                                // - Full_About-InvestorRelations role
                                resourceTypeKeys: ['Financial/Financial Report (Quarterly)'],
                            }}
                        >
                            <TechnicalResources backgroundColor={'white'} />
                        </PrefilteredResourceLibraryItemsProvider>
                    )}
                </CustomTabPanel>
                <CustomTabPanel value={url} index={2}>
                    {location.pathname.includes('InvestorConference') && (
                        <PrefilteredResourceLibraryItemsProvider
                            initialFilters={{
                                // On change of resourceTypeKeys, please also update the related Pimcore role
                                // - Full_About-InvestorRelations role
                                resourceTypeKeys: [
                                    // 'Financial/Investor Conference (Presentation)',
                                    'Financial/Investor Conference',
                                    'Financial/Earnings Conference',
                                    // 'Financial/Investor Conference (Audio)',
                                ],
                            }}
                        >
                            <TechnicalResources backgroundColor={'white'} />
                        </PrefilteredResourceLibraryItemsProvider>
                    )}
                </CustomTabPanel>

                {location.pathname.includes('ShareholderMeetings') && (
                    <PrefilteredResourceLibraryItemsProvider
                        initialFilters={{
                            // On change of resourceTypeKeys, please also update the related Pimcore role
                            // - Full_About-InvestorRelations role
                            resourceTypeKeys: [
                                // 'Financial/Shareholder Information (will be retired)',
                                'Financial/Shareholder Meeting',
                                // 'Financial/Shareholder Meeting Minutes',
                                // 'Financial/Shareholder Meeting Notice',
                                // 'Financial/Investor Conference (Audio)',
                                // 'Financial/Investor Conference (Presentation)',
                            ],
                        }}
                    >
                        <TechnicalResources backgroundColor={'white'} />
                    </PrefilteredResourceLibraryItemsProvider>
                )}
                <CustomTabPanel value={url} index={3}></CustomTabPanel>
                {location.pathname.includes('Newsroom') && (
                    <PrefilteredResourceLibraryItemsProvider
                        // On change of resourceTypeKeys, please also update the related Pimcore role
                        // - Full_About-InvestorRelations role
                        initialFilters={{
                            resourceTypeKeys: [
                                'News',
                                'News/Corporate',
                                'News/Financial',
                                'News/Product',
                            ],
                        }}
                    >
                        <TechnicalResources backgroundColor={'white'} />
                    </PrefilteredResourceLibraryItemsProvider>
                )}
            </Box>
        </div>
    );
};

export default InvestorRelationHome;
